import { useAppSelector } from 'app/hooks';
import StructureGrid from 'components/structure-page/2d/structure-grid';
import { STRUCTURE_GRID_SIZE } from 'data/constants';
import {
  CondenserPhotoData,
  CondenserProductType,
  ConfigurationType,
  FootprintPolygonsType,
  GridSpaceDataType,
  SystemOptionType,
  ZoneOutlineData,
  ZoneType
} from 'data/types';
import { memo, ReactNode, useMemo } from 'react';
import {
  buildConcealedDuctLayoutsForConfiguration,
  ZoneConcealedDuctLayout
} from 'utils/concealed-duct-geometry';
import { getEffectiveFootprintPolygons } from 'utils/footprint-polygons';
import { getGridSpacesForLevel } from 'utils/grid';
import { showsLayoutDiagramDuctRuns } from 'utils/layout-diagram-duct-runs';
import { isPtacIndoorFace } from 'utils/ptac-selected-system';
import { keepDuctRunLayoutsForSystem } from 'utils/system-duct-run-zones';
import { resolveSystemFloorplanConfiguration } from 'utils/system-floorplan-configuration';
import { buildSeriesDisplayName } from 'utils/system-products';
import {
  calculateZoneTotalBTUs,
  generateZoneColor,
  getZoneSpaceShapes
} from 'utils/zone-outline';

const EQUIPMENT_FLOORPLAN_PADDING_TILES = 6;
const CONDENSER_TILE_HALF = (3 * STRUCTURE_GRID_SIZE) / 2;
const CONDENSER_STROKE_COLOR = '#6b7280';

const PRODUCT_IMAGE_BASE_URL =
  'https://images.alpinehomeair.com/300x200/uwm/photos';

export type SystemFloorplanOverlayContext = Readonly<{
  configuration: ConfigurationType;
  system: SystemOptionType;
  levelIndex: number;
}>;

type SystemFloorplanProps = Readonly<{
  system: SystemOptionType;
  configuration?: ConfigurationType | null;
  // TODO: implement outdoor + indoor unit overlay and connector lines (see
  // mockup). The slot is plumbed but no callers render anything yet.
  renderEquipmentOverlay?: (ctx: SystemFloorplanOverlayContext) => ReactNode;
}>;

type ZoneEquipmentInfo = Readonly<{
  selectedProduct?: {
    photo?: string;
    name?: string;
    model?: string;
    seriesCode?: string | null;
    size?: string;
    type?: string;
  };
}>;

type LevelFloorplanProps = Readonly<{
  levelLabel: string;
  levelUniqueId: string;
  levelIndex: number;
  gridSpaces: GridSpaceDataType[];
  footprintPolygons: FootprintPolygonsType;
  configuration: ConfigurationType;
  system: SystemOptionType;
  concealedDuctLayouts: Map<string, ZoneConcealedDuctLayout>;
  ductRunLayouts: ZoneConcealedDuctLayout[];
  renderEquipmentOverlay?: (ctx: SystemFloorplanOverlayContext) => ReactNode;
}>;

function LevelFloorplanView({
  levelLabel,
  levelUniqueId,
  levelIndex,
  gridSpaces,
  footprintPolygons,
  configuration,
  system,
  concealedDuctLayouts,
  ductRunLayouts,
  renderEquipmentOverlay
}: LevelFloorplanProps) {
  const equipmentByZoneId = useMemo(() => {
    const map = new Map<string, ZoneEquipmentInfo>();
    system.configuration.condensers.forEach((condenser) => {
      condenser.zones.forEach((zone) => {
        if (isPtacIndoorFace(zone.selectedProduct)) return;
        map.set(zone.uniqueId, {
          selectedProduct: zone.selectedProduct ?? undefined
        });
      });
    });
    return map;
  }, [system.configuration.condensers]);

  const condenserProductByCondenserId = useMemo(() => {
    const map = new Map<string, CondenserProductType>();
    system.configuration.condensers.forEach((c) => {
      if (c.selectedProduct) {
        map.set(c.uniqueId, c.selectedProduct);
      }
    });
    return map;
  }, [system.configuration.condensers]);

  const condenserPhotos = useMemo<CondenserPhotoData[]>(() => {
    const photos: CondenserPhotoData[] = [];
    configuration.condensers.forEach((c) => {
      if (c.levelUniqueId !== levelUniqueId) return;
      if (!c.position) return;
      const product = condenserProductByCondenserId.get(c.uniqueId);
      if (!product?.photo) return;
      const descriptors = product.coolingBTU
        ? [`${product.coolingBTU.toLocaleString()} BTU`]
        : [];
      photos.push({
        condenserId: c.uniqueId,
        position: {
          x: c.position.x * STRUCTURE_GRID_SIZE + CONDENSER_TILE_HALF,
          y: c.position.y * STRUCTURE_GRID_SIZE + CONDENSER_TILE_HALF
        },
        photoUrl: `${PRODUCT_IMAGE_BASE_URL}/${product.photo}`,
        name: buildSeriesDisplayName(product),
        descriptors,
        strokeColor: CONDENSER_STROKE_COLOR
      });
    });
    return photos;
  }, [configuration.condensers, condenserProductByCondenserId, levelUniqueId]);

  const zoneOutlines = useMemo<ZoneOutlineData[]>(() => {
    const allZones: ZoneType[] = configuration.condensers.flatMap(
      (c) => c.zones
    );
    const zoneIdToGlobalIndex = new Map(
      allZones.map((zone, i) => [zone.uniqueId, i])
    );

    const outlines: ZoneOutlineData[] = [];
    configuration.condensers.forEach((condenser, condenserIndex) => {
      condenser.zones.forEach((zone) => {
        if (zone.zoneItems.length === 0) return;
        const spaceShapes = getZoneSpaceShapes(zone, gridSpaces);
        if (spaceShapes.length === 0) return;
        const colorIndex = zoneIdToGlobalIndex.get(zone.uniqueId) ?? 0;
        const equipment = equipmentByZoneId.get(zone.uniqueId);
        const ductLayout = concealedDuctLayouts.get(zone.uniqueId);
        const airHandlerPositionPx =
          ductLayout?.originLevelIndex === levelIndex
            ? ductLayout.originPx
            : undefined;
        outlines.push({
          zoneId: zone.uniqueId,
          zoneName: zone.name,
          condenserIndex: condenserIndex + 1,
          BTUs: calculateZoneTotalBTUs(zone),
          spacePositions: spaceShapes.map((s) => s.position),
          spaceShapes,
          color: generateZoneColor(colorIndex),
          airHandlerPhotoUrl: equipment?.selectedProduct?.photo
            ? `${PRODUCT_IMAGE_BASE_URL}/${equipment.selectedProduct.photo}`
            : undefined,
          airHandlerName: buildSeriesDisplayName(equipment?.selectedProduct),
          airHandlerSize: equipment?.selectedProduct?.size,
          airHandlerType: equipment?.selectedProduct?.type,
          ...(airHandlerPositionPx ? { airHandlerPositionPx } : {})
        });
      });
    });
    return outlines;
  }, [
    configuration,
    gridSpaces,
    equipmentByZoneId,
    concealedDuctLayouts,
    levelIndex
  ]);

  return (
    <div className='overflow-hidden rounded-lg border border-gray-200'>
      <div className='px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500'>
        {levelLabel || `Floor ${levelIndex + 1}`}
      </div>
      <div className='relative h-[380px] w-full'>
        <StructureGrid
          gridSpaces={gridSpaces}
          objects={[]}
          mode='static'
          zoneOutlines={zoneOutlines}
          condenserPhotos={condenserPhotos}
          concealedDuctLayouts={ductRunLayouts}
          currentLevelIndex={levelIndex}
          readOnly
          showSectionHighlighting
          autoFit
          autoFitPaddingTiles={EQUIPMENT_FLOORPLAN_PADDING_TILES}
          gridLinesOpacity={0}
          noTopRounding
          footprintPolygons={footprintPolygons}
        />
        {renderEquipmentOverlay?.({
          configuration,
          system,
          levelIndex
        })}
      </div>
    </div>
  );
}

const LevelFloorplan = memo(LevelFloorplanView);

function SystemFloorplan({
  system,
  configuration,
  renderEquipmentOverlay
}: SystemFloorplanProps) {
  const structure = useAppSelector((state) => state.structure.structure);
  const existingSystems = useAppSelector(
    (state) => state.structure.existingSystems
  );
  const configurations = useAppSelector(
    (state) => state.configurations.configurations
  );
  const zoneDuctModes = useAppSelector((state) => state.ui.zoneDuctModes);

  const sourceConfiguration = useMemo<ConfigurationType | null>(() => {
    if (configuration !== undefined) return configuration;
    return resolveSystemFloorplanConfiguration({
      system,
      configurations,
      existingSystems,
      structure
    });
  }, [configuration, system, configurations, existingSystems, structure]);

  const levels = useMemo(() => [...structure], [structure]);

  const concealedDuctLayouts = useMemo(
    () =>
      buildConcealedDuctLayoutsForConfiguration(
        sourceConfiguration ?? undefined,
        levels,
        (levelIndex) => getGridSpacesForLevel(levels, levelIndex),
        zoneDuctModes
      ),
    [sourceConfiguration, levels, zoneDuctModes]
  );

  const ductRunLayouts = useMemo(
    () =>
      sourceConfiguration && showsLayoutDiagramDuctRuns(sourceConfiguration)
        ? keepDuctRunLayoutsForSystem(
            [...concealedDuctLayouts.values()],
            system.configuration
          )
        : [],
    [sourceConfiguration, concealedDuctLayouts, system.configuration]
  );

  const levelEntries = useMemo(() => {
    if (!sourceConfiguration) return [];
    const allZones = sourceConfiguration.condensers.flatMap((c) => c.zones);
    const allZonedSpaceIds = new Set(
      allZones.flatMap((z) => z.zoneItems.map((item) => item.structureItemId))
    );

    return levels
      .map((level, levelIndex) => {
        const gridSpaces = getGridSpacesForLevel(levels, levelIndex);
        const hasZonedSpaces = gridSpaces.some((space) =>
          allZonedSpaceIds.has(space.spaceId)
        );
        if (!hasZonedSpaces) return null;
        return { level, levelIndex, gridSpaces };
      })
      .filter(
        (
          e
        ): e is {
          level: (typeof levels)[number];
          levelIndex: number;
          gridSpaces: GridSpaceDataType[];
        } => e !== null
      );
  }, [levels, sourceConfiguration]);

  if (!sourceConfiguration || levelEntries.length === 0) {
    return (
      <div className='rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-400'>
        No floorplan available for this system.
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-4'>
      {levelEntries.map(({ level, levelIndex, gridSpaces }) => (
        <LevelFloorplan
          key={level.uniqueId}
          levelLabel={level.label}
          levelUniqueId={level.uniqueId}
          levelIndex={levelIndex}
          gridSpaces={gridSpaces}
          footprintPolygons={getEffectiveFootprintPolygons(level)}
          configuration={sourceConfiguration}
          system={system}
          concealedDuctLayouts={concealedDuctLayouts}
          ductRunLayouts={ductRunLayouts}
          renderEquipmentOverlay={renderEquipmentOverlay}
        />
      ))}
    </div>
  );
}

export default memo(SystemFloorplan);
